<template>
  <div class="bb-settings-categories-project-step">
    <draggable
      v-model="stepsList"
      v-bind="dragOptions(stepsList)"
      @end="dragEnd"
      @start="dragStart"
      :move="onMove"
    >
      <template v-for="(el, index) in stepsList">
        <ExpenseCategoriesListOfProjectStepItem
          :hover.sync="hover"
          :draggable="draggable"
          :item="el"
          :index="index"
          :key="el._id"
          @onDelete="onDelete"
          @onEdit="onEdit"
          @onOpenColorPicker="onOpenColorPicker"
          @resetError="resetError"
          @onShowInfo="onShowInfo"
        />
      </template>
    </draggable>
    <div
      class="bb-settings-categories-project-step__button"
      @click="onAddStep"
    >
      + Add step
    </div>
    <transition name="bounce">
      <BBColorPicker
        v-if="isShowColorPicker"
        :popupPosition="popupPosition"
        :defaultColor="currentItem.color"
        :isShowColorPicker.sync="isShowColorPicker"
        @onChangeActiveColor="onChangeActiveColor"
      />
    </transition>
    <transition name="bounce">
      <div
        v-if="isShowInfo"
        :style="popupStyle"
      >
        <BBPopupInfo
          customClass="type"
          text="Unfortunately, it cannot be deleted, since there is linked equipment in this step"
        />
      </div>
    </transition>
  </div>
</template>

<script>

import { uniqueId } from 'lodash'
import draggable from 'vuedraggable'
import StepServiceProjectApi from '@/api/StepServiceProject/api'
import ExpenseCategoriesListOfProjectStepItem
  // eslint-disable-next-line max-len
  from '@/components/Settings/ExpenseCategories/ExpenseCategoriesListOfProject/ExpenseCategoriesListOfProjectStep/ExpenseCategoriesListOfProjectStepItem'
import BBColorPicker from '@/components/Main/BBColorPicker/BBColorPicker'
import BBPopupInfo from '@/components/Main/BBPopupInfo/BBPopupInfo'
import createBodyTypeServiceProject from '@/mixin/createBodyTypeServiceProject'

export default {
  name: 'ExpenseCategoriesListOfProjectStep',
  components: {
    BBColorPicker,
    draggable,
    ExpenseCategoriesListOfProjectStepItem,
    BBPopupInfo
  },
  mixins: [createBodyTypeServiceProject],
  props: {
    item: {
      type: Object,
      default: () => ({})
    }
  },
  data: () => ({
    isShowColorPicker: false,
    popupPosition: {},
    currentItem: {},
    draggable: false,
    hover: '',
    isShowInfo: false,
    popupStyle: {}
  }),
  computed: {
    userInfo () {
      return this.$store.getters.userInfo
    },
    stepsList: {
      get () {
        const { steps = [] } = this.item
        return steps.filter((el) => !el.isRemove)
      },
      set (data) {
        this.$set(this.item, 'steps', data)
      }
    }
  },
  methods: {
    onShowInfo (target) {
      this.isShowInfo = target
      if (!target) return
      const pos = target.getBoundingClientRect()
      const { left, width, top, height } = pos
      this.popupStyle = {
        position: 'fixed',
        left: `${left + (width / 2)}px`,
        top: `${top + height + 8}px`
      }
    },
    dragOptions () {
      return {
        handle: '.bb-settings-categories-project-step-item__drop',
        animation: 200,
        disabled: false
      }
    },
    onMove (item) {
      this.draggable = true
      const { draggedContext } = item
      const { futureIndex } = draggedContext
      setTimeout(() => {
        this.hover = this.stepsList[futureIndex]._id
      }, 0)
    },
    dragStart () {
      this.hover = ''
    },
    dragEnd ({newIndex}) {
      this.draggable = false
      const stepOne = this.stepsList[newIndex]
      stepOne.index = newIndex + 1
      this.updatePositionIndex(this.stepsList)
      setTimeout(() => {
        this.hover = this.stepsList[newIndex]._id
      }, 0)
      const { showSave } = this.item
      if (showSave) return
      this.swapStep()
    },
    async swapStep () {
      const steps = this.stepsList.map((el) => el._id)
      try {
        await StepServiceProjectApi.swapTypeServiceProject({steps})
      } catch (err) {
        console.error(err)
      }
    },
    totalPercent () {
      return this.stepsList.reduce((accumulator, item) => {
        const { percent } = item
        return accumulator + percent
      }, 0)
    },
    onAddStep () {
      const { id: user } = this.userInfo
      const { _id } = this.item
      const totalPercent = this.totalPercent()

      const step = {
        name: '',
        percent: totalPercent >= 100 ? 0 : 100 - totalPercent,
        color: '13, 123, 196',
        index: this.stepsList.length + 1,
        typeServiceProject: _id,
        user,
        isNew: true,
        isEdit: true,
        isEditPercent: true,
        _id: uniqueId()
      }
      this.$set(this.item, 'showSave', true)
      this.$set(this.item, 'isErrorMinStep', false)
      this.item.steps.push(step)
    },
    isValid (steps) {
      if (!steps.length) return false
      const isError = steps.some((el) => el.error)
      if (isError) return true
      const { totalPercent = 0 } = this.item
      if (totalPercent !== 100) return true
      if (steps.length === 1) {
        this.$set(this.item, 'isErrorMinStep', true)
        return true
      }
    },
    async onSave (item) {
      const { noRemoveSteps, ...all } = this.createBodyStep(item)
      if (this.isValid(noRemoveSteps)) return
      this.$set(item, 'showSave', false)

      try {
        const { data } = await StepServiceProjectApi.manyEditStepServiceProject(all)
        item.steps = data
      } catch (err) {
        console.error(err)
      }
    },
    onEdit () {
      this.$set(this.item, 'showSave', true)
    },
    resetError () {
      this.stepsList.forEach((el) => {
        el.error = ''
      })
    },
    onDelete (item, index) {
      this.$set(this.item, 'showSave', true)
      this.$set(this.stepsList[index], 'percent', 0)
      this.$set(this.stepsList[index], 'isRemove', true)
    },
    onChangeActiveColor (key) {
      this.isShowColorPicker = false
      const { indexRow } = this.currentItem
      this.stepsList[indexRow].color = key
      this.stepsList[indexRow].isEditColor = true
      this.$set(this.item, 'showSave', true)
    },
    onOpenColorPicker (item, index, { target }) {
      const { innerHeight } = window
      const { top, left, height } = target.getBoundingClientRect()
      const colorPickerWidth = 198
      const colorPickerHeight = 138
      const topDynamic = innerHeight < top + colorPickerHeight + height ? top - colorPickerHeight : top + height

      this.popupPosition = {
        position: 'fixed',
        left: `${left - colorPickerWidth}px`,
        top: `${topDynamic}px`
      }
      this.isShowColorPicker = !this.isShowColorPicker
      this.currentItem = {...item, indexRow: index}
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../../../../../sass/variables";

  .bb-settings-categories-project-step {

    &__button {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 34px;
      border-radius: 5px;
      border: 1px dashed $color-denim;
      margin: 16px 14px 16px 14px;
      font-size: 13px;
      line-height: 16px;
      font-weight: 500;
      color: $color-denim;
      cursor: pointer;
      transition: 0.2s;

      &_disabled {
        pointer-events: none;
        border-color: $color-silver;
        color: $color-gray;
      }

      &:hover {
        opacity: 0.8;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/Settings/ExpenseCategories/ExpenseCategoriesListOfProject/ExpenseCategoriesListOfProjectStep/ExpenseCategoriesListOfProjectStep.vue